import React, { useState, useEffect } from 'react';

export const LoadingSpinner = ({ message = "Loading...", subMessage = "", logoUrl = null }) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      {/* Organization logo if provided */}
      {logoUrl && (
        <img 
          src={logoUrl} 
          alt="Loading" 
          className="h-16 w-auto object-contain mb-6"
        />
      )}
      
      {/* Rotating spinner */}
      <div className="relative">
        <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
        <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
      </div>
      
      {/* Main message */}
      <p className="mt-6 text-lg font-bold text-gray-800 text-center">
        {message}
      </p>
      
      {/* Sub message */}
      {subMessage && (
        <p className="mt-2 text-sm text-gray-500 text-center max-w-md">
          {subMessage}
        </p>
      )}
    </div>
  );
};

export const HighTrafficLoading = ({ logoUrl = null }) => {
  const [showHighTrafficMessage, setShowHighTrafficMessage] = useState(false);
  
  useEffect(() => {
    // Show high traffic message after 3 seconds
    const timer = setTimeout(() => {
      setShowHighTrafficMessage(true);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <LoadingSpinner 
      message="Please wait..."
      subMessage={showHighTrafficMessage ? "Due to high demand, this may take a few moments" : ""}
      logoUrl={logoUrl}
    />
  );
};

// Branded loading overlay for 503/high traffic scenarios
export const BrandedRetryLoading = ({ logoUrl = null, retryCount = 0, maxRetries = 3 }) => {
  const [dots, setDots] = useState('');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(d => d.length >= 3 ? '' : d + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="fixed inset-0 bg-white/95 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="flex flex-col items-center justify-center p-8 max-w-md text-center">
        {/* Organization logo */}
        {logoUrl && (
          <img 
            src={logoUrl} 
            alt="" 
            className="h-20 w-auto object-contain mb-8"
          />
        )}
        
        {/* Animated spinner */}
        <div className="relative mb-6">
          <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-green-500 rounded-full border-t-transparent animate-spin"></div>
        </div>
        
        {/* Message */}
        <p className="text-xl font-semibold text-gray-800">
          High demand{dots}
        </p>
        <p className="mt-2 text-gray-500">
          We're experiencing high traffic. Your request is being processed.
        </p>
        
        {/* Retry indicator */}
        {retryCount > 0 && (
          <p className="mt-4 text-sm text-gray-400">
            Attempt {retryCount} of {maxRetries}
          </p>
        )}
      </div>
    </div>
  );
};

export default LoadingSpinner;
